import React, { Component } from 'react';
import {
    Panel,
    PanelType,
    PrimaryButton,
    DefaultButton,
    TextField,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Toggle,
    IconButton,
    Pivot,
    PivotItem
} from '@fluentui/react';
import { IFileProvisionConfig, DestinationType, destinationOptions } from './types';
import { createConfigService, updateConfigService } from './services';

interface IProps {
    isOpen: boolean;
    config: IFileProvisionConfig | null;
    onDismiss: () => void;
    onSave: (config: IFileProvisionConfig) => void;
}

interface IState {
    formData: IFileProvisionConfig;
    errorMessage: string;
    successMessage: string;
    isSaving: boolean;
    isTesting: boolean;
}

class ProvisionConfigEditPanel extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            formData: this._getInitialFormData(),
            errorMessage: '',
            successMessage: '',
            isSaving: false,
            isTesting: false
        };
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props.config !== prevProps.config || this.props.isOpen !== prevProps.isOpen) {
            this.setState({
                formData: this._getInitialFormData(),
                errorMessage: '',
                successMessage: '',
                isSaving: false,
                isTesting: false
            });
        }
    }

    private _getInitialFormData = (): IFileProvisionConfig => {
        if (this.props.config) {
            return { ...this.props.config };
        }
        return {
            id: 0,
            destinationType: DestinationType.sharepoint,
            rawQuery: 'Configured by administrator',
            isActive: true,
            lastRunAt: null
        };
    };

    private _onFieldChange = (field: keyof IFileProvisionConfig) => (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ): void => {
        this.setState({
            formData: { ...this.state.formData, [field]: newValue || '' },
            errorMessage: ''
        });
    };

    private _onDropdownChange = (field: keyof IFileProvisionConfig) => (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption
    ): void => {
        if (option) {
            this.setState({
                formData: { ...this.state.formData, [field]: option.key as string }
            });
        }
    };

    private _onToggleChange = (field: keyof IFileProvisionConfig) => (
        event: React.MouseEvent<HTMLElement>,
        checked?: boolean
    ): void => {
        this.setState({
            formData: { ...this.state.formData, [field]: checked }
        });
    };

    private _validateForm = (): boolean => {
        const { formData } = this.state;

        if (!formData.destinationType) {
            this.setState({ errorMessage: 'Destination type is required' });
            return false;
        }

        if (!formData.authType) {
            this.setState({ errorMessage: 'Authentication type is required' });
            return false;
        }

        // Destination-specific validation
        if (formData.destinationType === DestinationType.sharepoint) {
            if (!formData.siteUrl || !formData.siteUrl.trim()) {
                this.setState({ errorMessage: 'SharePoint site URL is required' });
                return false;
            }
            if (!formData.driveId || !formData.driveId.trim()) {
                this.setState({ errorMessage: 'Drive ID is required' });
                return false;
            }
            if (!formData.folderPath || !formData.folderPath.trim()) {
                this.setState({ errorMessage: 'Folder Path is required' });
                return false;
            }
        } else if (formData.destinationType === DestinationType.blobContainer) {
            if (!formData.containerName || !formData.containerName.trim()) {
                this.setState({ errorMessage: 'Container name is required' });
                return false;
            }
            if (!formData.targetUri || !formData.targetUri.trim()) {
                this.setState({ errorMessage: 'Target URL is required' });
                return false;
            }
        }
        return true;
    };

    private _onSave = async (): Promise<void> => {
        if (!this._validateForm()) return;

        this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

        try {
            const { formData } = this.state;
            let savedConfig: IFileProvisionConfig;

            if (formData.id === 0) {
                savedConfig = await createConfigService(formData);
                console.log('Created new provision config with ID:', savedConfig);
            } else {
                savedConfig = await updateConfigService(formData);
            }

            this.props.onSave(savedConfig);
        } catch (error) {
            this.setState({
                errorMessage: 'Failed to save configuration. Please try again.',
                isSaving: false
            });
        }
    };

    private _renderGeneralFields = (): JSX.Element => {
        const { formData } = this.state;

        return (
            <div>
                <Dropdown
                    label="Destination Type"
                    selectedKey={formData.destinationType}
                    options={destinationOptions}
                    onChange={this._onDropdownChange('destinationType')}
                    required
                    styles={{ root: { marginBottom: 15 } }}
                />

                <Toggle
                    label="Active Status"
                    checked={formData.isActive}
                    onChange={this._onToggleChange('isActive')}
                    onText="Active"
                    offText="Inactive"
                    styles={{ root: { marginBottom: 15 } }}
                />
            </div>
        );
    };

    private _renderDestinationFields = (): JSX.Element => {
        const { formData } = this.state;
        return (
            <div>
                {formData.destinationType === DestinationType.sharepoint && (
                    <>
                        <TextField
                            label="SharePoint Site URL"
                            value={formData.siteUrl || ''}
                            onChange={this._onFieldChange('siteUrl')}
                            placeholder="https://contoso.sharepoint.com/sites/sitename"
                            required
                            styles={{ root: { marginBottom: 15 } }}
                        />
                        <TextField
                            label="Drive ID"
                            value={formData.driveId || ''}
                            onChange={this._onFieldChange('driveId')}
                            placeholder="b!xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            styles={{ root: { marginBottom: 15 } }}
                            required
                            description="SharePoint document library drive ID"
                        />
                        <TextField
                            label="Folder Path"
                            value={formData.folderPath || ''}
                            onChange={this._onFieldChange('folderPath')}
                            placeholder="/Documents/Reports/2025"
                            required
                            styles={{ root: { marginBottom: 15 } }}
                        />
                    </>
                )}

                {formData.destinationType === DestinationType.blobContainer && (
                    <>
                        <TextField
                            label="Container Name"
                            value={formData.containerName || ''}
                            onChange={this._onFieldChange('containerName')}
                            placeholder="container-name"
                            required
                            styles={{ root: { marginBottom: 15 } }}
                        />
                        <TextField
                            label="Target URI"
                            value={formData.targetUri || ''}
                            onChange={this._onFieldChange('targetUri')}
                            placeholder="https://storage.blob.core.windows.net/container"
                            required
                            styles={{ root: { marginBottom: 15 } }}
                        />
                    </>
                )}
            </div>
        );
    };

    private _renderScheduleFields = (): JSX.Element => {
        const { formData } = this.state;

        return (
            <div>
                <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 15 } }}>
                    <strong>Important:</strong> This frequency field is for information purposes only to sync with the WebJob settings.json file.
                    It does not change the scheduler or trigger the WebJob. Please update the settings.json file directly to modify the actual schedule.
                </MessageBar>

                <TextField
                    label="Frequency (Cron Expression)"
                    value={formData.frequencyCron || ''}
                    onChange={this._onFieldChange('frequencyCron')}
                    placeholder="0 0 3 * * 1 (e.g., Every Monday at 3:00 AM)"
                    styles={{ root: { marginBottom: 15 } }}
                    description="Format: second minute hour day month dayOfWeek or minute hour day month dayOfWeek"
                />

                <TextField
                    label="Notes"
                    value={formData.notes || ''}
                    onChange={this._onFieldChange('notes')}
                    placeholder="Add description or notes about this configuration"
                    multiline
                    rows={3}
                    styles={{ root: { marginBottom: 15 } }}
                />

                {formData.lastRunAt && (
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#f3f2f1',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#605e5c'
                    }}>
                        <strong>Last Run:</strong> {new Date(formData.lastRunAt + 'Z').toLocaleString()}
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { isOpen, onDismiss, config } = this.props;
        const { errorMessage, successMessage, isSaving, isTesting } = this.state;

        return (
            <Panel
                isOpen={isOpen}
                onDismiss={onDismiss}
                headerText={config && config.id !== 0 ? 'Edit Provision Configuration' : 'Add Provision Configuration'}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <PrimaryButton
                            onClick={this._onSave}
                            disabled={isSaving || isTesting}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </PrimaryButton>
                        <DefaultButton onClick={onDismiss} disabled={isSaving}>
                            Cancel
                        </DefaultButton>
                    </div>
                )}
                isFooterAtBottom={true}
                customWidth='600px'
                type={PanelType.medium}
                isLightDismiss={!isSaving}
            >
                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => this.setState({ errorMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                {successMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={false}
                        onDismiss={() => this.setState({ successMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {successMessage}
                    </MessageBar>
                )}

                <Pivot aria-label="Configuration sections">
                    <PivotItem headerText="General" itemIcon="Settings">
                        <div style={{ paddingTop: 15 }}>
                            {this._renderGeneralFields()}
                        </div>
                    </PivotItem>
                    <PivotItem headerText="Destination" itemIcon="MapPin">
                        <div style={{ paddingTop: 15 }}>
                            {this._renderDestinationFields()}
                        </div>
                    </PivotItem>
                    <PivotItem headerText="Schedule & Notes" itemIcon="Clock">
                        <div style={{ paddingTop: 15 }}>
                            {this._renderScheduleFields()}
                        </div>
                    </PivotItem>
                </Pivot>
            </Panel>
        );
    }
}

export default ProvisionConfigEditPanel;
